<template>
    <ConfirmModal
        :visible="visible"
        title="Site update conflicts with assignees"
        icon="fi-alert-circle text-danger"
        cancel-button-text="Cancel"
        :confirm-button-text="confirmButtonText"
        :confirm-disabled="confirming"
        @close="$emit('close')"
        @confirm="$emit('confirm')"
    >
        <div
            class="site-conflict-modal d-flex flex-column align-items-stretch w-100 text-start"
            style="gap: var(--space-3)"
        >
            <p class="site-conflict-modal__outcome mb-0">
                Moving these action items from
                <strong>{{ oldSiteName }}</strong>
                to
                <strong>{{ newSiteName }}</strong>
                will clear assignments for users who don't have access that
                site. The action items will remain open.
            </p>

            <div v-if="conflicts.length" class="d-flex flex-column w-100 gap-2">
                <div class="site-conflict-modal__disclosure menu-chevron">
                    <label
                        class="col-form-label mb-0 menu-chevron-label"
                        @click="toggleList"
                    >
                        Affected action items ({{ affectedCount }})
                    </label>
                    <button
                        type="button"
                        class="icon-btn icon-btn-white icon-btn-sm flex-shrink-0"
                        :aria-expanded="isListExpanded ? 'true' : 'false'"
                        aria-controls="site-conflict-list"
                        @click="toggleList"
                    >
                        <i
                            class="fi"
                            :class="
                                isListExpanded
                                    ? 'fi-chevron-down'
                                    : 'fi-chevron-right'
                            "
                            aria-hidden="true"
                        ></i>
                    </button>
                </div>

                <div
                    v-if="isListExpanded"
                    id="site-conflict-list"
                    class="site-conflict-modal__list"
                    role="region"
                    aria-label="Affected action items"
                >
                    <div
                        v-for="item in conflicts"
                        :key="item.action_point_hash || item.action_point_id"
                        class="site-conflict-modal__item"
                    >
                        <span class="site-conflict-modal__item-title">
                            {{ formatTitle(item.title) }}
                        </span>
                        <span class="site-conflict-modal__item-assignee">
                            {{ item.accountable_name }}
                        </span>
                    </div>

                    <p
                        v-if="hiddenCount > 0"
                        class="site-conflict-modal__more mb-0"
                    >
                        +{{ hiddenCount }} more not shown
                    </p>
                </div>
            </div>
        </div>
    </ConfirmModal>
</template>

<script>
import ConfirmModal from '@/components/ConfirmModal.vue';
import { formatConflictTitle } from '@/utils/actionPointAccountableConflict.js';

export default {
    name: 'ActionPointSiteConflictModal',
    components: { ConfirmModal },
    props: {
        visible: { type: Boolean, required: true },
        oldSiteName: { type: String, default: '' },
        newSiteName: { type: String, default: '' },
        affectedCount: { type: Number, default: 0 },
        conflicts: { type: Array, default: () => [] },
        hiddenCount: { type: Number, default: 0 },
        confirming: { type: Boolean, default: false },
    },
    emits: ['close', 'confirm'],
    data() {
        return {
            isListExpanded: false,
        };
    },
    computed: {
        confirmButtonText() {
            return this.confirming
                ? 'Updating...'
                : 'Update site and remove assignees';
        },
    },
    methods: {
        formatTitle(title) {
            return formatConflictTitle(title);
        },
        toggleList() {
            this.isListExpanded = !this.isListExpanded;
        },
    },
    watch: {
        visible(value) {
            if (value) {
                this.isListExpanded = false;
            }
        },
    },
};
</script>

<style scoped>
:global(.custom-modal:has(.site-conflict-modal)) {
    width: min(36rem, calc(100vw - 2rem));
    max-width: calc(100vw - 2rem);
}

.site-conflict-modal__outcome {
    font-size: 0.875rem;
    line-height: 1.5;
    color: var(--neutral-800);
    text-align: center;
}

.menu-chevron-label {
    cursor: pointer;
}

.site-conflict-modal__disclosure {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    gap: var(--space-2);
    text-align: left;
}

.site-conflict-modal__list {
    display: flex;
    flex-direction: column;
    gap: var(--space-2);
    max-height: min(40vh, 16rem);
    overflow-y: auto;
    padding: var(--space-3);
    background-color: var(--neutral-400);
    border-radius: var(--space-3);
    scrollbar-width: thin;
    scrollbar-color: var(--neutral-600) transparent;
}

.site-conflict-modal__item {
    display: flex;
    flex-direction: column;
    gap: var(--space-1);
    padding: var(--space-3);
    border: 0.0625rem solid var(--neutral-600);
    border-radius: var(--radius-default);
    background-color: var(--white);
}

.site-conflict-modal__item-title {
    font-size: 0.875rem;
    line-height: 1.35;
    color: var(--neutral-900);
    display: -webkit-box;
    overflow: hidden;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
}

.site-conflict-modal__item-assignee {
    font-size: 0.8125rem;
    font-weight: 600;
    line-height: 1.25;
    color: var(--neutral-800);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.site-conflict-modal__more {
    padding-top: var(--space-1);
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--neutral-800);
    text-align: center;
}

@media (max-width: 575.98px) {
    :global(.custom-modal:has(.site-conflict-modal)) {
        width: calc(100vw - 1rem);
        max-width: calc(100vw - 1rem);
    }
}
</style>
